import React, { useEffect, useRef, useState } from "react";
import {
  Buttons,
  ChevronLeft,
  Container,
  FirstItem,
  HeadRow,
  IconContainer,
  LastTableItem,
  Row,
  SectionTitle,
} from "./TabManage.styles";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  deleteCompanyBusinessUser,
  getCompanyUsers,
} from "../../../store/slices/company/asyncThunks";
import { ReactComponent as InviteUser } from "../../../assets/icons/person_add.svg";
import { ReactComponent as Edit } from "../../../assets/icons/edit-3.svg";
import UseAnimations from "react-useanimations";
import trash2 from "react-useanimations/lib/trash2";
import Button from "../../Button/Button";
import ModalConfirm from "../../ModalConfirm/ModalConfirm";
import InviteUserModal from "../../InviteUserModal/InviteUserModal";
import SearchInput from "../../Search/Search";
import MiniLoader from "../../MiniLoader/MiniLoader";
import openToast from "../../../utils/toast";
import Table from "../../Table/Table";
import { TableBodyRow } from "../../Table/Table.styles";
import TableItem from "../../Table/TableItem";
import PermissionsTable from "../../Table/PermissionsTable";
import {
  addRoleToUser,
  createBusinessUserRole,
  updateBusinessUserRole,
} from "../../../store/slices/businessUser/asyncThunks";
import {
  selectCreateBusinessUserRolePending,
  selectGetAllBusinessUserRolesPending,
  selectUpdateBusinessUserRolePending,
} from "../../../store/slices/businessUser/slice";

import { ReactComponent as Loader } from "../../../assets/icons/loader-white.svg";
import RoleModal from "../../RoleModal";
import { checkPermission } from "../../../utils/utils";
import { businessUserRoleAction } from "../../../utils/constants";

const TabManage = ({
  businessUser,
  searchQuery,
  setSearchQuery,
  handleGetInvitationLink,
  companyLoading,
  currentUsers,
  currentPage,
  handlePageChange,
  selectedUser,
  totalNumOfUsers,
  perPage,
  businessUserRoles,
  handleRolesPageChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const modalRef = useRef();
  const [inviteUserModal, setInviteUserModal] = useState(false);
  const toastId = useRef(null);
  const [manageRole, setManageRole] = useState(false);
  const [addRole, setAddRole] = useState(false);
  const [editPermissions, setEditPermissions] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleName, setRoleName] = useState("");
  const updateBusinessUserRolePending = useSelector(
    selectUpdateBusinessUserRolePending
  );
  const createBusinessUserRolePending = useSelector(
    selectCreateBusinessUserRolePending
  );
  const getAllBusinessUserRolesPending = useSelector(
    selectGetAllBusinessUserRolesPending
  );

  const [updateRoleUser, setUpdateRoleUser] = useState(null);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

  const openRoleModal = (user) => {
    setUpdateRoleUser(user);
    setIsRoleModalOpen(true);
  };

  const tableHeader = [
    {
      label: `${t("email")}`,
      sortable: true,
      key: "email",
    },
    {
      label: `${t("full-name")}`,
      sortable: true,
      key: "fullName",
    },
    {
      label: `${t("role")}`,
      sortable: true,
      key: "role",
    },
  ];

  const tableHeaderRole = [
    {
      label: `${t("role-name")}`,
      sortable: true,
      key: "roleName",
    },
    {
      label: `${t("num-of-users")}`,
      sortable: true,
      key: "numOfUsers",
    },
    {
      label: `${t("options")}`,
      sortable: false,
      key: "options",
    },
  ];

  useEffect(() => {
    if (selectedRole && editPermissions) {
      setPermissions(selectedRole.permissions);
    }
  }, [selectedRole, editPermissions]);

  const handlePermissionChange = (category, action) => {
    const permissionKey = `${action}_${category}`;
    const isExists = permissions.includes(permissionKey);

    if (isExists) {
      setPermissions((prev) => prev.filter((p) => p !== permissionKey));
    } else {
      setPermissions((prev) => [...prev, permissionKey]);
    }
  };

  const handleEditPermission = () => {
    if (
      !updateBusinessUserRolePending &&
      selectedRole &&
      permissions.length > 0
    ) {
      dispatch(
        updateBusinessUserRole({ id: selectedRole.id, body: { permissions } })
      )
        .unwrap()
        .then(() => {
          setEditPermissions(false);
          setSelectedRole(null);
          setPermissions([]);
        })
        .catch((err) => {});
    }
  };

  const handleCreatePermission = () => {
    if (roleName && permissions.length && !createBusinessUserRolePending) {
      dispatch(createBusinessUserRole({ name: roleName, permissions }))
        .unwrap()
        .then(() => {
          setAddRole(false);
          setManageRole(true);
          setPermissions([]);
          setRoleName("");
        })
        .catch((err) => {});
    }
  };

  const RoleCell = ({ role, onEdit }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <span>{role}</span>
      {checkPermission(
        businessUser,
        businessUserRoleAction.post_add_business_role
      ) && <Edit onClick={onEdit} style={{ cursor: "pointer" }} />}
    </div>
  );

  const handleAddRole = (businessUserRoleId, businessUserId) => {
    dispatch(addRoleToUser({ businessUserRoleId, businessUserId }))
      .unwrap()
      .then(() => {
        dispatch(getCompanyUsers())
          .unwrap()
          .then(() => {
            setUpdateRoleUser(null);
            setIsRoleModalOpen(false);
          })
          .catch((error) => {
            toast.error(t(error.message));
          });
      })
      .catch((err) => {});
  };

  return (
    <>
      <RoleModal
        isOpen={isRoleModalOpen}
        onClose={() => setIsRoleModalOpen(false)}
        onSelectRole={handleAddRole}
        businessUser={updateRoleUser}
      />
      <Container>
        <HeadRow>
          {!editPermissions ? (
            manageRole && (
              <Row>
                <IconContainer
                  onClick={() => {
                    setManageRole(false);
                  }}
                >
                  <ChevronLeft />
                </IconContainer>
                <SectionTitle>{t("manage-roles")}</SectionTitle>
              </Row>
            )
          ) : (
            <Row $fullWidth>
              <IconContainer
                onClick={() => {
                  setEditPermissions(false);
                  setSelectedRole(null);
                  setPermissions([]);
                }}
              >
                <ChevronLeft />
              </IconContainer>
              <SectionTitle $capitalize>{`${t("role")}: ${
                selectedRole.name
              }`}</SectionTitle>

              {permissions && permissions?.length > 0 ? (
                !updateBusinessUserRolePending ? (
                  <Button
                    style={{ marginLeft: "auto" }}
                    onClick={() => {
                      handleEditPermission();
                    }}
                    title={t("save")}
                  />
                ) : (
                  <Loader height={24} width={24} />
                )
              ) : (
                <Button
                  disabled
                  style={{ marginLeft: "auto" }}
                  onClick={() => {}}
                  title={t("save")}
                />
              )}
            </Row>
          )}

          {addRole && (
            <Row $fullWidth>
              <IconContainer
                onClick={() => {
                  setAddRole(false);
                  setManageRole(true);
                  setRoleName("");
                }}
              >
                <ChevronLeft />
              </IconContainer>
              <SectionTitle>{t("add-a-role")}</SectionTitle>

              {roleName && permissions?.length > 0 ? (
                !createBusinessUserRolePending ? (
                  <Button
                    style={{ marginLeft: "auto" }}
                    onClick={handleCreatePermission}
                    title={t("save")}
                  />
                ) : (
                  <Loader height={24} width={24} />
                )
              ) : (
                <Button
                  disabled
                  style={{ marginLeft: "auto" }}
                  onClick={() => {}}
                  title={t("save")}
                />
              )}
            </Row>
          )}

          <Buttons>
            {!editPermissions && !addRole && !manageRole && (
              <SearchInput
                placeholder={t("search-by-name-or-email")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            )}

            {manageRole &&
              !selectedRole &&
              checkPermission(
                businessUser,
                businessUserRoleAction.post_add_business_role
              ) && (
                <Button
                  onClick={() => {
                    setAddRole(true);
                    setManageRole(false);
                  }}
                  title={t("add-a-role")}
                />
              )}
          </Buttons>

          {!manageRole && !addRole && (
            <Buttons>
              {checkPermission(
                businessUser,
                businessUserRoleAction.get_business_role
              ) && (
                <Button
                  onClick={() => {
                    setManageRole(true);
                  }}
                  title={t("manage-roles")}
                />
              )}
              {(businessUser.role === "owner" ||
                businessUser.role === "superAdmin") && (
                <Button
                  onClick={() => {
                    setInviteUserModal(true);
                    handleGetInvitationLink();
                  }}
                  title={t("invite-users")}
                  leftIcon={<InviteUser style={{ marginRight: 10 }} />}
                />
              )}
            </Buttons>
          )}
        </HeadRow>

        {editPermissions && (
          <PermissionsTable
            permissions={permissions}
            onPermissionChange={handlePermissionChange}
          />
        )}

        {addRole && (
          <PermissionsTable
            addRole
            roleName={roleName}
            setRoleName={setRoleName}
            permissions={permissions}
            onPermissionChange={handlePermissionChange}
          />
        )}

        {!addRole &&
          !editPermissions &&
          (manageRole ? (
            !companyLoading ? (
              <>
                <Table
                  marginTop={40}
                  headerInformation={tableHeaderRole}
                  totalCount={businessUserRoles?.total}
                  currentPage={+businessUserRoles?.page}
                  setPage={handleRolesPageChange}
                  param={"page"}
                  perPage={perPage}
                  noDataContent={t("no-business-roles")}
                  dataLoading={getAllBusinessUserRolesPending}
                  lastColumnAligned
                  centerText
                >
                  {businessUserRoles?.data?.map((role, key) => {
                    return (
                      <TableBodyRow key={key} onClick={() => {}} $offClick>
                        <FirstItem
                          showHover
                          data={role.name || "/"}
                          $capitalize
                        />
                        <TableItem center data={role.numOfUsers} />
                        <LastTableItem
                          $offClick
                          data={
                            <Row>
                              <Edit
                                fill="#484888"
                                height={30}
                                style={{ marginBottom: 7, cursor: "pointer" }}
                                width={30}
                                onClick={() => {
                                  setEditPermissions(true);
                                  setSelectedRole(role);
                                }}
                              />
                              {/* <UseAnimations
                                onClick={(e) => {}}
                                className="delete"
                                animation={trash2}
                                size={30}
                                strokeColor="#484888"
                              /> */}
                            </Row>
                          }
                        />
                      </TableBodyRow>
                    );
                  })}
                </Table>
              </>
            ) : (
              <div style={{ marginTop: 100 }}>
                <MiniLoader />
              </div>
            )
          ) : !companyLoading ? (
            <>
              <Table
                marginTop={40}
                headerInformation={tableHeader}
                totalCount={totalNumOfUsers}
                currentPage={+currentPage}
                setPage={handlePageChange}
                param={"page"}
                perPage={perPage}
                noDataContent={t("no-users")}
                dataLoading={false}
                lastColumnAligned
                centerText
              >
                {currentUsers.map((user, key) => {
                  return (
                    <TableBodyRow key={key} onClick={() => {}} $offClick>
                      <FirstItem
                        hoverText={user?.email}
                        showHover
                        data={
                          user.email && user.email.length > 25
                            ? user.email.slice(0, 25) + "..."
                            : user.email || "/"
                        }
                      />
                      <TableItem center data={t(`${user.fullName}`)} />
                      <LastTableItem
                        $offClick
                        data={
                          <RoleCell
                            role={user.role || "/"}
                            onEdit={() => openRoleModal(user)}
                          />
                        }
                        $capitalize
                      />
                    </TableBodyRow>
                  );
                })}
              </Table>
            </>
          ) : (
            <div style={{ marginTop: 100 }}>
              <MiniLoader />
            </div>
          ))}
      </Container>

      <InviteUserModal
        open={inviteUserModal}
        close={() => setInviteUserModal(false)}
      />
      <ModalConfirm
        modalRef={modalRef}
        onClickNo={() => {
          modalRef.current.hide();
        }}
        onClickYes={() => {
          dispatch(deleteCompanyBusinessUser(selectedUser))
            .unwrap()
            .then(() => toast.success(t("User deleted successfully.")))
            .catch((error) => {
              openToast(
                toastId,
                "delete-company-user",
                "error",
                t(error.message)
              );
            });
          modalRef.current.hide();
        }}
      />
    </>
  );
};

export default TabManage;
